<template>
  <a href="#main-content" class="ux-skip-link">Skip to main content</a>
  <!-- Full-screen layout for learning page -->
  <div v-if="isLearningPage">
    <router-view :key="route.path" />
  </div>
  
  <!-- Normal layout with navbar for other pages -->
  <div v-else :class="['ux-shell page-container min-h-screen flex flex-col', { '!pt-0': isAuthPage }]">
    <nav ref="navigationEl" aria-label="Main navigation" @keydown.esc="closeNavigation" class="app-navbar border-b border-gray-200 bg-white dark:bg-gray-900 dark:border-gray-800">
      <div class="navbar-container">
        <!-- Logo & Mobile Toggle Row -->
        <div class="ux-navbar-brand">
          <router-link to="/" class="navbar-logo flex items-center">
            <img v-if="navbarLogoLoaded" :src="navbarLogoSrc" :alt="appName" class="h-8 w-auto" @error="navbarLogoLoaded = false" />
            <span v-else>{{ appName }}</span>
          </router-link>
          <router-link to="/courses" class="ux-mobile-catalog">Courses</router-link>
          <!-- Mobile menu button -->
          <button 
            ref="mobileMenuTrigger"
            @click="mobileMenuOpen = !mobileMenuOpen" 
            class="ux-mobile-menu-toggle lg:hidden p-2 rounded-md transition-colors"
            :aria-label="mobileMenuOpen ? 'Close navigation' : 'Open navigation'" :aria-expanded="mobileMenuOpen" aria-controls="mobile-navigation"
          >
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path v-if="!mobileMenuOpen" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16"/>
              <path v-else stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
          
        </div>
        
        <!-- Desktop Navigation -->
        <div class="navbar-left ux-desktop-navigation">
          <router-link to="/" class="navbar-link">Home</router-link>
          <router-link to="/about" class="navbar-link">About</router-link>
          <router-link to="/courses" :data-active="route.path.startsWith('/courses')" class="navbar-link">Courses</router-link>
          <router-link v-if="isLoggedIn" to="/account" class="navbar-link">My learning</router-link>
        </div>
        
        <!-- Desktop User Menu -->
        <div class="ux-desktop-account items-center gap-3">
          <button @click="toggleTheme" class="p-2 rounded-md border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800" aria-label="Toggle theme">
            <svg v-if="themeMode === 'dark'" class="w-5 h-5 text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><circle cx="12" cy="12" r="4" stroke-width="1.5"/><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M12 3v2.25M12 18.75V21M21 12h-2.25M3 12H5.25M16.95 7.05l1.59-1.59M5.46 18.54l1.59-1.59M16.95 16.95l1.59 1.59M5.46 5.46l1.59 1.59"/></svg>
            <svg v-else class="w-5 h-5 text-gray-700 dark:text-gray-200" fill="currentColor" viewBox="0 0 20 20"><path d="M17.293 13.293A8 8 0 016.707 2.707a8 8 0 1010.586 10.586z"/></svg>
          </button>
          <template v-if="isLoggedIn">
            <div ref="userMenuEl" class="navbar-right" @mouseenter="handleDropdownEnter" @mouseleave="handleDropdownLeave">
              <button ref="userMenuTrigger" class="user-dropdown-trigger" @click="showDropdown = !showDropdown" :aria-expanded="showDropdown" aria-controls="user-navigation">
                <span class="user-initials-badge" aria-hidden="true">
                  {{ userInitials }}
                </span>
                <span class="user-name-text">{{ username }}</span>
                <span class="sr-only">, account options</span>
                <svg class="dropdown-icon" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7"/></svg>
              </button>
              <div v-if="showDropdown" id="user-navigation" class="user-dropdown-menu">
                <router-link to="/account" class="dropdown-menu-item">Account</router-link>
                <router-link v-if="isLecturer" to="/admin/generate-course" class="dropdown-menu-item">Generate from notes</router-link>
                <router-link v-if="isLecturer" to="/admin/collections" class="dropdown-menu-item">Manage collections</router-link>
                <button v-if="isLecturer" @click="openWagtail" class="dropdown-menu-button">Create Course (Wagtail)</button>
                <button @click="logout" class="dropdown-menu-button">Logout</button>
              </div>
            </div>
          </template>
          <template v-else>
            <router-link to="/login" class="navbar-link">Login</router-link>
            <router-link to="/register" class="ux-button ux-button-primary">Create account</router-link>
          </template>
        </div>
      </div>
      
      <!-- Mobile Menu -->
      <div v-if="mobileMenuOpen" id="mobile-navigation" class="lg:hidden border-t border-gray-200 bg-white dark:bg-gray-900 dark:border-gray-800">
        <div class="px-4 pt-2 pb-3 space-y-1">
          <router-link v-for="link in [{ path: '/', label: 'Home' }, { path: '/about', label: 'About' }, { path: '/courses', label: 'Courses' }]" :key="link.path" :to="link.path" @click="mobileMenuOpen = false" class="block px-3 py-2 rounded-md">{{ link.label }}</router-link>
          <div v-if="isLoggedIn">
            <div class="flex items-center px-3 py-2 mb-2">
              <span class="user-initials-badge mr-2">{{ userInitials }}</span>
              <span class="text-sm font-medium text-gray-900">{{ username }}</span>
            </div>
            <button @click="toggleTheme" class="w-full text-left px-3 py-2 rounded-md text-base font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors flex items-center gap-2">
              <svg v-if="themeMode === 'dark'" class="w-5 h-5 text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><circle cx="12" cy="12" r="4" stroke-width="1.5"/><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M12 3v2.25M12 18.75V21M21 12h-2.25M3 12H5.25M16.95 7.05l1.59-1.59M5.46 18.54l1.59-1.59M16.95 16.95l1.59 1.59M5.46 5.46l1.59 1.59"/></svg>
              <svg v-else class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path d="M17.293 13.293A8 8 0 016.707 2.707a8 8 0 1010.586 10.586z"/></svg>
              <span>{{ themeMode === 'dark' ? 'Light Mode' : 'Dark Mode' }}</span>
            </button>
            <router-link @click="mobileMenuOpen = false" to="/account" class="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-indigo-600 hover:bg-gray-50 transition-colors">My learning</router-link>
            <router-link v-if="isLecturer" @click="mobileMenuOpen = false" to="/admin/generate-course" class="block px-3 py-2 rounded-md">Generate from notes</router-link>
            <router-link v-if="isLecturer" @click="mobileMenuOpen = false" to="/admin/collections" class="block px-3 py-2 rounded-md">Manage collections</router-link>
            <button v-if="isLecturer" @click="openWagtail(); mobileMenuOpen = false" class="w-full text-left px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-indigo-600 hover:bg-gray-50 transition-colors">Create Course (Wagtail)</button>
            <button @click="logout(); mobileMenuOpen = false" class="w-full text-left px-3 py-2 rounded-md text-base font-medium text-red-600 hover:bg-red-50 transition-colors">Logout</button>
          </div>
          <div v-else class="border-t border-gray-200 pt-3 mt-3 space-y-1">
            <button @click="toggleTheme" class="w-full text-left px-3 py-2 rounded-md text-base font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors flex items-center gap-2">
              <svg v-if="themeMode === 'dark'" class="w-5 h-5 text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><circle cx="12" cy="12" r="4" stroke-width="1.5"/><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M12 3v2.25M12 18.75V21M21 12h-2.25M3 12H5.25M16.95 7.05l1.59-1.59M5.46 18.54l1.59-1.59M16.95 16.95l1.59 1.59M5.46 5.46l1.59 1.59"/></svg>
              <svg v-else class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path d="M17.293 13.293A8 8 0 016.707 2.707a8 8 0 1010.586 10.586z"/></svg>
              <span>{{ themeMode === 'dark' ? 'Light Mode' : 'Dark Mode' }}</span>
            </button>
            <router-link @click="mobileMenuOpen = false" to="/login" class="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-indigo-600 hover:bg-gray-50 transition-colors">Login</router-link>
            <router-link @click="mobileMenuOpen = false" to="/register" class="block px-3 py-2 rounded-md text-base font-medium text-indigo-600 hover:bg-indigo-50 transition-colors font-semibold">Register</router-link>
          </div>
        </div>
      </div>
    </nav>
    <main id="main-content" tabindex="-1" :class="['flex-1 min-h-0', { 'content-wrapper': !isAuthPage, 'flex': isAuthPage }]">
      <router-view :key="route.path" />
    </main>
    <AppFooter v-if="!isAuthPage" />
    <CookieConsent v-if="!isAuthPage" />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch, onBeforeUnmount, nextTick } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useToast } from 'vue-toastification'
import { getApiBase } from '@/utils/url'
import { issueWagtailTicket } from '@/services/auth'
import { getAccessToken, invalidateSession, TOKEN_KEY, REFRESH_TOKEN_KEY } from '@/auth/storage'
import { useCoursesStore } from '@/stores/courses'
import { useEnrollmentsStore } from '@/stores/enrollments'
import { useAuthStore } from '@/stores/auth'
import { useProgressStore } from '@/stores/progress'
import { useThemeStore } from '@/stores/theme'
import AppFooter from '@/components/AppFooter.vue'
import CookieConsent from '@/components/CookieConsent.vue'
import { appName } from '@/utils/appInfo'

const toast = useToast()
const enrollmentsStore = useEnrollmentsStore()
const auth = useAuthStore()
const progressStore = useProgressStore()

const isLoggedIn = computed(() => auth.isAuthenticated)
const username = computed(() => auth.username)
const showDropdown = ref(false)
const mobileMenuOpen = ref(false)
const mobileMenuTrigger = ref(null)
const userMenuEl = ref(null)
const userMenuTrigger = ref(null)
const navigationEl = ref(null)
const closeNavigation = () => {
  if (showDropdown.value) { showDropdown.value = false; userMenuTrigger.value?.focus() }
  else if (mobileMenuOpen.value) { mobileMenuOpen.value = false; mobileMenuTrigger.value?.focus() }
}
const dismissNavigation = event => {
  if (!userMenuEl.value?.contains(event.target)) showDropdown.value = false
  if (!navigationEl.value?.contains(event.target)) mobileMenuOpen.value = false
}
const router = useRouter()
const route = useRoute()
const isLecturer = computed(() => auth.isLecturer)
const dropdownTimer = ref(null)
const theme = useThemeStore()
const themeMode = computed(() => theme.mode === 'system' ? (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light') : theme.mode)

const navbarLogoLoaded = ref(true)
const navbarLogoSrc = computed(() => themeMode.value === 'dark' ? '/web-logo-dark.svg' : '/web-logo-light.svg')

// Check if current route is the learning page
const isLearningPage = computed(() => route.path.startsWith('/learn/'))
const isAuthPage = computed(() => ['/login', '/register', '/forgot-password'].includes(route.path) || route.path.startsWith('/reset-password/'))

const getToken = () => getAccessToken()

const userInitials = computed(() => {
  if (!username.value) return ''
  return username.value.split(' ').map(n => n[0]).join('').toUpperCase()
})

const checkAuth = async () => {
  try { await auth.ensureLoaded() } catch (_) {}
}

const onStorageChange = (event) => {
  if (event.key !== null && ![TOKEN_KEY, REFRESH_TOKEN_KEY].includes(event.key)) return
  invalidateSession()
  auth.reset()
  enrollmentsStore.clear()
  progressStore.clear()
  useCoursesStore().clear()
  checkAuth()
}

const handleDropdownEnter = () => {
  // Clear any existing timer
  if (dropdownTimer.value) {
    clearTimeout(dropdownTimer.value)
    dropdownTimer.value = null
  }
  showDropdown.value = true
}

const handleDropdownLeave = () => {
  // Set a 3-second timer before closing
  dropdownTimer.value = setTimeout(() => {
    if (!userMenuEl.value?.contains(document.activeElement)) showDropdown.value = false
    dropdownTimer.value = null
  }, 3000) // 3 seconds delay
}

const logout = () => {
  if (dropdownTimer.value) {
    clearTimeout(dropdownTimer.value)
    dropdownTimer.value = null
  }
  showDropdown.value = false
  auth.logout()
  enrollmentsStore.clear()
  progressStore.clear()
  toast.success('Logged out successfully')
  router.push('/')
}



onMounted(() => {
  checkAuth()
  window.addEventListener('storage', onStorageChange)
  document.addEventListener('pointerdown', dismissNavigation)
})

// Cleanup on unmount
onBeforeUnmount(() => {
  window.removeEventListener('storage', onStorageChange)
  document.removeEventListener('pointerdown', dismissNavigation)
  // Clear dropdown timer
  if (dropdownTimer.value) {
    clearTimeout(dropdownTimer.value)
  }
})

// Watch for route changes to update isLearningPage and close mobile menu
watch(() => route.path, async () => {
  mobileMenuOpen.value = false // Close mobile menu on route change
  showDropdown.value = false
  await nextTick()
  document.getElementById('main-content')?.focus({ preventScroll: true })
})

const openWagtail = async () => {
  if (!getToken()) return
  const popup = window.open('about:blank', '_blank')
  try {
    const result = await issueWagtailTicket()
    const url = `${getApiBase()}wagtail/sso/?ticket=${encodeURIComponent(result.ticket)}`
    if (popup) { popup.opener = null; popup.location.replace(url) }
    else window.location.assign(url)
  } catch (_) {
    popup?.close()
    toast.error('Unable to open the course editor. Please try again.')
  }
}

const toggleTheme = () => {
  const next = themeMode.value === 'dark' ? 'light' : 'dark'
  theme.setMode(next)
}
</script>
